import { DateValue } from '@internationalized/date';
import { FinanceAggregateType } from '@rematter/frontend-lib-api-client';
import { atom, useAtom } from 'jotai';

import { financeOutboxEventCountsQueryAtom } from '../_graphql/queryFinanceOutboxEventCounts';
import { financeOutboxEventsQueryAtom } from '../_graphql/queryFinanceOutboxEvents';

import {
  dateToZonedDateTimeEnd,
  dateToZonedDateTimeStart,
} from '~/shared/utils/dateTime';

export type FinanceAuditTab = 'events' | 'inbox';

export const $financeAuditTab = atom<FinanceAuditTab>('events');

export const $financeAggregateTypeFilter = atom<
  FinanceAggregateType | undefined
>(undefined);

export const $financeAggregateIdFilter = atom<string | undefined>(undefined);

export const $financePagination = atom<{ offset: number; limit: number }>({
  offset: 0,
  limit: 50,
});

export const $selectedFinanceEventId = atom<string | undefined>(undefined);

export const $financeDateRangeFilter = atom<{
  start: DateValue;
  end: DateValue;
} | null>(null);

export const $remoteFinanceOutboxEvents = financeOutboxEventsQueryAtom(
  (get) => {
    const aggregateType = get($financeAggregateTypeFilter);
    const aggregateId = get($financeAggregateIdFilter);
    const pagination = get($financePagination);
    const dateRange = get($financeDateRangeFilter);

    return {
      variables: {
        aggregateTypes: aggregateType ? [aggregateType] : undefined,
        aggregateId,
        pagination,
        createdAtRange:
          dateRange?.start && dateRange?.end
            ? {
                start: dateToZonedDateTimeStart(dateRange.start),
                end: dateToZonedDateTimeEnd(dateRange.end),
              }
            : undefined,
      },
    };
  },
);

export const $remoteFinanceOutboxEventCounts =
  financeOutboxEventCountsQueryAtom();

export const useFinanceAuditTab = () => {
  const [tab, setTab] = useAtom($financeAuditTab);
  return [tab, setTab] as const;
};

export const useFinanceAggregateTypeFilter = () => {
  const [filter, setFilter] = useAtom($financeAggregateTypeFilter);
  const [, setAggregateId] = useAtom($financeAggregateIdFilter);

  const setFilterAndResetId = (value: FinanceAggregateType | undefined) => {
    setFilter(value);
    setAggregateId(undefined);
  };

  return [filter, setFilterAndResetId] as const;
};

export const useFinanceAggregateIdFilter = () => {
  const [filter, setFilter] = useAtom($financeAggregateIdFilter);
  return [filter, setFilter] as const;
};

export const useFinancePagination = () => {
  const [pagination, setPagination] = useAtom($financePagination);
  return [pagination, setPagination] as const;
};

export const useSelectedFinanceEventId = () => {
  const [selectedId, setSelectedId] = useAtom($selectedFinanceEventId);
  return [selectedId, setSelectedId] as const;
};

export const useFinanceDateRangeFilter = () => {
  const [filter, setFilter] = useAtom($financeDateRangeFilter);
  return [filter, setFilter] as const;
};
